import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { loginAction, logOutAction, setLastVisitedLink } from "redux/authSlice";
import { updatePreference } from "redux/preferencesSlice";
import {
  convertResourceToObjectFormat,
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import defaultImage from "assets/icons1/avatars/default.png";
import urls from "global/constants/UrlConstants";
import Clarity from "@microsoft/clarity";
import { samlLogout, SamlLogoutResponse } from "./AuthService";

const SSO_REQUESTED_EMAIL_KEY = "sso_requested_email";

export const setSsoRequestedEmail = (email: string) => {
  try {
    sessionStorage.setItem(SSO_REQUESTED_EMAIL_KEY, email);
  } catch (error) {
    console.warn("setSsoRequestedEmail: sessionStorage unavailable", error);
  }
};

export const getSsoRequestedEmail = (): string | null => {
  try {
    return sessionStorage.getItem(SSO_REQUESTED_EMAIL_KEY);
  } catch {
    return null;
  }
};

export const clearSsoRequestedEmail = () => {
  try {
    sessionStorage.removeItem(SSO_REQUESTED_EMAIL_KEY);
  } catch (error) {
    console.warn("clearSsoRequestedEmail: sessionStorage unavailable", error);
  }
};

const SSO_CODE_EXCHANGED_KEY = "sso_code_exchanged";

export const markSsoCodeExchanged = (code: string) => {
  try {
    sessionStorage.setItem(SSO_CODE_EXCHANGED_KEY, code);
  } catch (error) {
    console.warn("markSsoCodeExchanged: sessionStorage unavailable", error);
  }
};

export const wasSsoCodeExchanged = (code: string): boolean => {
  try {
    return sessionStorage.getItem(SSO_CODE_EXCHANGED_KEY) === code;
  } catch {
    return false;
  }
};

/** Called on a failed exchange so the marker doesn't permanently block a legitimate retry
 *  if the same code ever reaches this effect again. */
export const clearSsoCodeExchanged = () => {
  try {
    sessionStorage.removeItem(SSO_CODE_EXCHANGED_KEY);
  } catch (error) {
    console.warn("clearSsoCodeExchanged: sessionStorage unavailable", error);
  }
};

const SSO_LOGOUT_REDIRECT_KEY = "sso_logout_redirect_pending";

const markSsoLogoutRedirect = () => {
  try {
    sessionStorage.setItem(SSO_LOGOUT_REDIRECT_KEY, "1");
  } catch (error) {
    console.warn("markSsoLogoutRedirect: sessionStorage unavailable", error);
  }
};

export const consumeSsoLogoutRedirect = (): boolean => {
  try {
    const pending = sessionStorage.getItem(SSO_LOGOUT_REDIRECT_KEY) === "1";
    sessionStorage.removeItem(SSO_LOGOUT_REDIRECT_KEY);
    return pending;
  } catch {
    return false;
  }
};

/**
 * Human-readable account name off a login / SSO-exchange response, as opposed
 * to `account` — the tenant key (e.g. "sign") the rest of the app identifies
 * the account by.
 *
 * The API sends it as `accountDisplay` ("SoftSages E-Sign"); the other two keys
 * are accepted because the SSO exchange and the account-details endpoint spell
 * it differently (`displayName` on read, `accountDisplayName` on update). Falls
 * back to "" so callers can decide whether to show the tenant key instead.
 */
export const getAccountDisplayName = (user: any): string =>
  user?.accountDisplay ?? user?.accountDisplayName ?? user?.displayName ?? "";

export const buildBaseLoginPayload = (
  user: any,
  userType?: any,
  isSsoLogin: boolean = false,
) => ({
  authenticated: true,
  accessToken: user.authToken,
  refreshToken: user.refreshToken,
  userName: user.name,
  email: (user.email ?? "").toLowerCase(),
  userRole: user.role,
  profileImage: isTruthy(user.profilePicture)
    ? "data:image/png;base64," + user.profilePicture
    : defaultImage,
  deviceId: "",
  account: user.account,
  accountDisplayName: getAccountDisplayName(user),
  userId: user.id,
  isTrial: user.trial,
  lastLogin: user.lastLogin,
  type: userType?.message ?? "",
  isSsoLogin,
});

export const shouldShowPasswordExpiryPage = (user: any): boolean =>
  user.pwdExpiryEnable &&
  (user.pwdExpiryRemainingDays <= 0 ||
    (user.pwdExpiryRemainingDays > 0 && user.pwdExpiryRemainingDays <= 7));

export const buildExpiryPayload = (user: any, basePayload: any) =>
  user.pwdExpiryRemainingDays > 0
    ? {
        ...basePayload,
        resources: convertResourceToObjectFormat(user.resources),
        pwdExpiryEnable: true,
        pwdExpiryRemainingDays: user.pwdExpiryRemainingDays,
      }
    : {
        pwdExpiryEnable: true,
        pwdExpiryRemainingDays: user.pwdExpiryRemainingDays,
        email: user.email,
        accessToken: user.authToken,
        refreshToken: user.refreshToken,
        resources: {},
      };

export const handlePasswordExpiryRedirect = (
  user: any,
  basePayload: any,
  dispatch: any,
): boolean => {
  if (!shouldShowPasswordExpiryPage(user)) return false;
  const payload = buildExpiryPayload(user, basePayload);
  dispatch(loginAction(payload));
  history.push(viewpaths.expiryPasswordViewPath);
  return true;
};

export const establishSession = (
  user: any,
  dispatch: any,
  current?: string,
  password?: string,
  loginSource: "password" | "sso" = "password",
): boolean => {
  // Carried through so a user who needs MFA mid-login still lands back on
  // `current` (e.g. the MCP auth bridge) once they finish verifying, instead
  // of falling through to the dashboard.
  const redirectSearch =
    current && current !== viewpaths.loginViewPath
      ? "?redirectionUrl=" + encodeURIComponent(current)
      : "";

  if (user?.multiFactorLogin) {
    history.push({
      pathname: viewpaths.verifyTOTPViewPath,
      search: redirectSearch,
      state: { email: user.email },
    });
    return false;
  }

  if (user?.mfaSetupRequired) {
    history.push({
      pathname: viewpaths.setupMFAViewPath,
      search: redirectSearch,
      state: {
        email: user.email,
        password,
        token: user.authToken,
      },
    });
    return false;
  }

  const baseLoginPayload = buildBaseLoginPayload(
    user,
    undefined,
    loginSource === "sso",
  );

  if (urls.PROD) {
    Clarity.setTag("account", user.account);
    Clarity.setTag("email", user.email);
    Clarity.identify(user.email, user.account, user.id, user.account);
  }

  dispatch(
    updatePreference({
      layout: "vertical",
      theme: "dark",
    }),
  );

  if (handlePasswordExpiryRedirect(user, baseLoginPayload, dispatch)) return false;

  /*
    A 2xx with an empty (or unparseable) body leaves `makeCall` returning
    `undefined`, so there is no session to build. That is a server fault, not an
    account without permissions — and it must be caught *before*
    `convertResourceToObjectFormat`, whose falsy-resources path dispatches a
    logout and redirects to the landing page, hiding the cause.
  */
  if (!isTruthy(user)) {
    openErrorNotification(
      "The server returned an empty response. Please try again in a moment.",
    );
    return false;
  }

  const formattedResources = convertResourceToObjectFormat(user.resources);
  if (isTruthy(formattedResources)) {
    dispatch(
      loginAction({
        ...baseLoginPayload,
        resources: formattedResources,
      }),
    );
    // On the SSO path, replace rather than push: the entry being left behind is /sso/callback,
    // which should not be reachable via Back at all.
    const destination = current ?? viewpaths.dashboardViewPath;
    if (loginSource === "sso") {
      history.replace(destination);
    } else {
      history.push(destination);
    }
    return true;
  }

  // Reached only when the response parsed but carried no usable permissions.
  openErrorNotification(
    "Your account has no access permissions assigned. Please contact your workspace admin.",
  );
  return false;
};

const submitSamlLogoutForm = (url: string, parameters: Record<string, string>) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.style.display = "none";
  Object.entries(parameters).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const followSamlLogoutResponse = (
  response: SamlLogoutResponse,
  fallbackPath: string,
) => {
  if (response.sloRequired && response.url) {
    if (response.method === "POST" && response.parameters) {
      submitSamlLogoutForm(response.url, response.parameters);
      return;
    }
    if (response.method === "REDIRECT") {
      window.location.href = response.url;
      return;
    }
  }

  history.push(fallbackPath);
};

export const logoutWithSso = async (dispatch: any): Promise<boolean> => {
  let response: SamlLogoutResponse;
  try {
    response = await samlLogout();
  } catch (error: any) {
    handleErrorMessage(error);
    return false;
  }

  const path = history.location.pathname + history.location.search;
  dispatch(logOutAction({}));
  dispatch(setLastVisitedLink(path));

  const redirectsToIdp = Boolean(response.sloRequired && response.url);
  if (redirectsToIdp) markSsoLogoutRedirect();
  followSamlLogoutResponse(response, viewpaths.landingViewPath);
  return redirectsToIdp;
};

export const switchSsoAccount = async (
  loginHint: string,
  token: string,
): Promise<void> => {
  let response: SamlLogoutResponse;
  try {
    response = await samlLogout(loginHint, token);
  } catch (error: any) {
    handleErrorMessage(error);
    return;
  }

  followSamlLogoutResponse(response, viewpaths.loginViewPath);
};
